import React, { useCallback, useEffect, useRef, useState } from 'react';
import { resolveDocTitle } from '../../utils/evaluationPlanDocTitle';
import ModalDocumentActionButtons from '../common/ModalDocumentActionButtons';
import type { EvaluationPlanAttachment, EvaluationPlanAttachmentSlot } from '../../types/evaluationPlan';
import { EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS } from '../../types/evaluationPlan';
import type { EvaluationLevelDisplayLabels, EvaluationLevelStepCount } from '../../types/evaluationPlan';
import { DEFAULT_LEVEL_STEP_COUNT } from '../../types/evaluationPlan';
import {
  createDefaultEvaluationPlanWebDocument,
  parseEvaluationPlanWebDocumentData,
  syncWebDocumentLevelRowLabels,
  type EvaluationPlanWebDocumentData,
} from '../../types/evaluationPlanWebDocument';
import { saveEvaluationPlanWebDocumentAsExcel } from '../../utils/evaluationPlanWebDocumentExcelExport';
import {
  printEvaluationPlanWebDocumentFromRoot,
  saveEvaluationPlanWebDocumentAsPdfFromRoot,
} from '../../utils/evaluationPlanWebDocumentDocumentExport';
import EvaluationPlanWebDocumentPageView from './EvaluationPlanWebDocumentPageView';

function sanitizeFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

const EvaluationPlanWebDocumentViewerModal: React.FC<{
  open: boolean;
  slot: EvaluationPlanAttachmentSlot | null;
  attachment: EvaluationPlanAttachment | null;
  levelStepCount?: EvaluationLevelStepCount;
  levelDisplayLabels?: EvaluationLevelDisplayLabels | null;
  onClose: () => void;
  stackAboveDocumentList?: boolean;
  /** 평가계획 성취기준의 과목명(제목 자동 표시용) */
  subjectNames?: string[];
}> = ({
  open,
  slot,
  attachment,
  levelStepCount = DEFAULT_LEVEL_STEP_COUNT,
  levelDisplayLabels,
  onClose,
  stackAboveDocumentList = false,
  subjectNames,
}) => {
  const exportRootRef = useRef<HTMLDivElement | null>(null);
  const [documentData, setDocumentData] = useState<EvaluationPlanWebDocumentData | null>(null);
  const [exportBusy, setExportBusy] = useState(false);

  useEffect(() => {
    if (!open || !slot) {
      setDocumentData(null);
      return;
    }

    const parsed = parseEvaluationPlanWebDocumentData(attachment?.form_data?.web_document);
    const base = parsed ?? createDefaultEvaluationPlanWebDocument(slot, levelStepCount, levelDisplayLabels);
    const synced = syncWebDocumentLevelRowLabels(base, levelStepCount, levelDisplayLabels);
    setDocumentData({
      ...synced,
      title: resolveDocTitle(slot === 'criterion' ? 'criterion' : 'sheet', synced.title, subjectNames),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slot, attachment, levelStepCount, levelDisplayLabels, (subjectNames ?? []).join('|')]);

  const slotLabel = slot ? EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot] : '';

  const buildExportFileNameBase = useCallback(() => {
    const titleToken = sanitizeFileToken(documentData?.title?.trim() || '평가기준안');
    const slotToken = sanitizeFileToken(slotLabel || '웹문서');
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    return `평가기준안_${slotToken}_${titleToken}_${dateLabel}`;
  }, [documentData?.title, slotLabel]);

  const handleSaveExcel = useCallback(async () => {
    if (!documentData) return;
    setExportBusy(true);
    try {
      await saveEvaluationPlanWebDocumentAsExcel({
        data: documentData,
        fileName: `${buildExportFileNameBase()}.xlsx`,
      });
    } catch (error) {
      console.error('평가 기준안 엑셀 저장 실패:', error);
      alert('엑셀 저장에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [buildExportFileNameBase, documentData]);

  const handleSavePdf = useCallback(async () => {
    const root = exportRootRef.current;
    if (!root || !documentData) return;
    setExportBusy(true);
    try {
      await saveEvaluationPlanWebDocumentAsPdfFromRoot({
        root,
        fileName: `${buildExportFileNameBase()}.pdf`,
      });
    } catch (error) {
      console.error('평가 기준안 PDF 저장 실패:', error);
      alert('PDF 저장에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [buildExportFileNameBase, documentData]);

  const handlePrint = useCallback(() => {
    const root = exportRootRef.current;
    if (!root || !documentData) return;
    setExportBusy(true);
    try {
      printEvaluationPlanWebDocumentFromRoot(
        root,
        documentData.title.trim() || `${slotLabel} 웹 문서`
      );
    } catch (error) {
      console.error('평가 기준안 프린트 실패:', error);
      alert('프린트에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [documentData, slotLabel]);

  if (!open || !slot || !documentData) return null;

  return (
    <div
      className={
        'cp-modal-backdrop cp-evaluation-plan-web-doc-backdrop cp-evaluation-plan-web-doc-viewer-backdrop' +
        (stackAboveDocumentList ? ' cp-evaluation-plan-viewer-backdrop--above-doc-list' : '')
      }
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="cp-modal cp-evaluation-plan-web-doc-modal cp-evaluation-plan-web-doc-viewer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-evaluation-plan-web-doc-viewer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-modal-document-header cp-evaluation-plan-web-doc-viewer-header">
          <h3 id="cp-evaluation-plan-web-doc-viewer-title">{slotLabel} 웹 문서</h3>
          <ModalDocumentActionButtons
            onSaveExcel={() => void handleSaveExcel()}
            onSavePdf={() => void handleSavePdf()}
            onPrint={() => void handlePrint()}
            busy={exportBusy}
          />
        </div>

        <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

        <div className="cp-modal-body cp-evaluation-plan-web-doc-body cp-evaluation-plan-web-doc-viewer-body">
          <div className="cp-evaluation-plan-web-doc-page-scroll">
            <EvaluationPlanWebDocumentPageView data={documentData} exportRootRef={exportRootRef} />
          </div>
        </div>

        <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

        <div className="cp-modal-footer cp-evaluation-plan-web-doc-modal-footer">
          <button type="button" className="cp-btn-primary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPlanWebDocumentViewerModal;
